import { Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "./components";
import { bilingual, useText, useLang } from "./i18n";
import {
  type LegalPart,
  LEGAL_SECTIONS,
  LEGAL_VERSIONS,
  TERMS_VERSION,
  legalDate,
  legalVersion,
} from "../shared/legal";

const TEXT = bilingual({
  en: {
    updated: (day: string) => `Last updated: ${day}`,
    archived: (from: string, until: string) =>
      `Earlier version, in force from ${from} to ${until}.`,
    current: "See the current version",
    earlier: "Earlier versions:",
  },
  es: {
    updated: (day: string) => `Última actualización: ${day}`,
    archived: (from: string, until: string) =>
      `Versión anterior, vigente del ${from} al ${until}.`,
    current: "Ver la versión actual",
    earlier: "Versiones anteriores:",
  },
});

/**
 * Legal notice, privacy, terms of use and cookies, in the version in force; `?v=<version>` shows an earlier
 * one (shared/legal.ts keeps them all).
 */
export function Legal() {
  const lang = useLang(),
    t = useText(TEXT);
  const [params] = useSearchParams();
  const shown = legalVersion(params.get("v")) ?? legalVersion(TERMS_VERSION)!;
  const text = shown.text[lang];
  const day = (d: string) => legalDate(d, lang);
  const earlier = LEGAL_VERSIONS.filter((v) => v.until);
  return (
    <div className="legal">
      <header className="legal-top">
        <Link to="/" aria-label="Foco">
          <Logo />
        </Link>
      </header>
      <article className="legal-body">
        <h1 className="h1">{text.title}</h1>
        <p className="legal-updated">{t.updated(day(shown.version))}</p>
        {shown.until && (
          <p className="legal-archived" role="note">
            {t.archived(day(shown.version), day(shown.until))}{" "}
            <Link to="/legal">{t.current}</Link>
          </p>
        )}
        <nav className="legal-nav">
          {LEGAL_SECTIONS.map((id) => (
            <a key={id} href={`#${id}`}>
              {text.sections[id].title}
            </a>
          ))}
        </nav>
        {LEGAL_SECTIONS.map((id) => (
          <Fragment key={id}>
            <h2 className="h2" id={id}>
              {text.sections[id].title}
            </h2>
            {text.sections[id].parts.map((part, i) => (
              <LegalPartView key={i} {...part} />
            ))}
          </Fragment>
        ))}
        {earlier.length > 0 && (
          <p className="legal-foot legal-versions">
            {t.earlier}{" "}
            {earlier.map((v, i) => (
              <Fragment key={v.version}>
                {i > 0 && ", "}
                <Link to={`/legal?v=${v.version}`}>
                  {day(v.version)} – {day(v.until!)}
                </Link>
              </Fragment>
            ))}
          </p>
        )}
        <p className="legal-foot">© 2026 AIKIT REVENUE, S.L.U.</p>
      </article>
    </div>
  );
}

function LegalPartView({ h, p, list }: LegalPart) {
  return (
    <>
      {h && <h3>{h}</h3>}
      {p && <p>{p}</p>}
      {list && (
        <ul>
          {list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </>
  );
}
